import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faTimes,
  faDownload,
  faSearch,
  faCalendarAlt,
  faHospital,
  faUsers,
  faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import './BillingReportPreviewModal.css';

const BillingReportPreviewModal = ({
  isOpen,
  onClose,
  reportData,
  onDownloadCsv,
  onProceedToSend
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const {
    headers = [],
    rows = [],
    practiceName = '',
    program = 'RPM',
    billingPeriod = '',
    count = 0
  } = reportData || {};

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const term = searchTerm.toLowerCase().trim();
    return rows.filter((row) =>
      row.some((cell) => String(cell != null ? cell : '').toLowerCase().includes(term))
    );
  }, [rows, searchTerm]);

  if (!isOpen) return null;

  return (
    <div className="report-preview-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="report-preview-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="report-preview-header">
          <div className="report-preview-title-wrap">
            <span className="report-preview-icon">
              <FontAwesomeIcon icon={faFileAlt} />
            </span>
            <div>
              <h3 className="report-preview-title">Billing Report Preview</h3>
              <p className="report-preview-subtitle">
                Review the report data before sending the email to recipients
              </p>
            </div>
          </div>
          <button
            type="button"
            className="report-preview-close"
            onClick={onClose}
            aria-label="Close Preview"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Metadata Banner */}
        <div className="report-preview-meta-bar">
          <div className="report-preview-meta-item">
            <FontAwesomeIcon icon={faHospital} className="report-meta-icon" />
            <span className="report-meta-label">Practice:</span>
            <span className="report-meta-val" title={practiceName || 'All Practices'}>
              {practiceName || 'All Practices'}
            </span>
          </div>

          <div className="report-preview-meta-item">
            <span className="report-meta-label">Program:</span>
            <span className={`report-prog-badge prog-${String(program).toLowerCase()}`}>
              {program}
            </span>
          </div>

          {billingPeriod && (
            <div className="report-preview-meta-item">
              <FontAwesomeIcon icon={faCalendarAlt} className="report-meta-icon" />
              <span className="report-meta-label">Period:</span>
              <span className="report-meta-val">{billingPeriod}</span>
            </div>
          )}

          <div className="report-preview-meta-item">
            <FontAwesomeIcon icon={faUsers} className="report-meta-icon" />
            <span className="report-meta-label">Total Patients:</span>
            <span className="report-meta-count">{count || rows.length}</span>
          </div>
        </div>

        {/* Toolbar with Search and Row Count */}
        <div className="report-preview-toolbar">
          <div className="report-preview-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="report-search-icon" />
            <input
              type="text"
              placeholder="Search in preview..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="report-preview-search"
            />
            {searchTerm && (
              <button
                type="button"
                className="report-search-clear"
                onClick={() => setSearchTerm('')}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>

          <div className="report-preview-count-label">
            Showing <strong>{filteredRows.length}</strong> of <strong>{rows.length}</strong> row{rows.length === 1 ? '' : 's'}
            {headers.length > 0 && ` (${headers.length} columns)`}
          </div>
        </div>

        {/* Scrollable Table */}
        <div className="report-preview-table-container">
          {filteredRows.length === 0 ? (
            <div className="report-preview-empty">
              {searchTerm ? 'No patient records match your search filter.' : 'No data available to preview.'}
            </div>
          ) : (
            <table className="report-preview-table">
              <thead>
                <tr>
                  {headers.map((header, idx) => (
                    <th key={`${header}-${idx}`}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, rowIdx) => (
                  <tr key={`row-${rowIdx}`}>
                    {row.map((cell, colIdx) => {
                      const headerName = String(headers[colIdx] || '').toLowerCase();
                      const isStatus = headerName === 'status';
                      const isCountOrTime =
                        headerName.includes('min') ||
                        headerName.includes('time') ||
                        headerName.includes('days') ||
                        headerName === '#';

                      return (
                        <td
                          key={`col-${colIdx}`}
                          className={`${isCountOrTime ? 'cell-numeric' : ''} ${isStatus ? 'cell-status' : ''}`}
                        >
                          {isStatus ? (
                            <span className={`preview-status-pill status-${String(cell || '').toLowerCase()}`}>
                              {cell || 'N/A'}
                            </span>
                          ) : (
                            String(cell != null && cell !== '' ? cell : '—')
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="report-preview-footer">
          <div className="report-preview-footer-note">
            This preview displays the exact report data that will be sent to the email recipients.
          </div>
          <div className="report-preview-actions">
            {onDownloadCsv && (
              <button
                type="button"
                className="btn-preview-download"
                onClick={onDownloadCsv}
                title="Download preview as CSV file"
              >
                <FontAwesomeIcon icon={faDownload} /> Download CSV
              </button>
            )}
            <button
              type="button"
              className="btn-preview-close"
              onClick={onClose}
            >
              Close Preview
            </button>
            {onProceedToSend && (
              <button
                type="button"
                className="btn-preview-send"
                onClick={onProceedToSend}
              >
                <FontAwesomeIcon icon={faPaperPlane} /> Return to Send Email
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingReportPreviewModal;
